/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
/* eslint-disable complexity */
import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import type GlobalModel from 'echarts/lib/model/Global';
import type ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import { type default as CartesianAxisModel } from 'echarts/lib/coord/cartesian/AxisModel';
import { type default as CartesianAxisView } from 'echarts/lib/component/axis/CartesianAxisView';
import type { Group, Text } from 'echarts/lib/util/graphic';

// 已经适用的轴线类型
// 【需要注意】：目前单轴只在水平方向上有应用：词流图，垂直方向上可能有未知问题
const usageAxisTypes = ['xAxis', 'yAxis', 'singleAxis'];

function installDvAlignEdge(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('afterupdate', (ecModel: GlobalModel, api: ExtensionAPI) => {
    ecModel.eachComponent((componentType: string, compModel: CartesianAxisModel) => {
      const hideOverlap = compModel?.get('axisLabel')?.hideOverlap;
      const dvAlignEdge = compModel?.get('axisLabel')?.dvAlignEdge;
      const inverse = compModel?.get('inverse');
      if (!usageAxisTypes.includes(componentType) || !compModel?.get('show') || !dvAlignEdge) {
        return;
      }
      const { axis } = compModel;
      const { orient } = axis;
      const isHor = orient === 'horizontal' || componentType === 'xAxis';
      const compView = api.getViewOfComponentModel(compModel) as CartesianAxisView;
      const labels: Text[] = [];
      const hasAnimation = compModel.get('animation') !== false;
      const animationConfig: any = {
        duration: compModel.get('animationDurationUpdate'),
        easing: compModel.get('animationEasingUpdate'),
        delay: compModel.get('animationDelayUpdate')
      };

      // @ts-ignore
      (compView.group as Group).traverse(childEl => {
        if ((childEl.anid || '').startsWith('label')) {
          labels.push(childEl as Text);
        }
      });

      if (labels.length < 2) {
        return;
      }

      let firstChild = labels[0];
      let lastChild = labels[labels.length - 1];
      let extentCoord = [
        compModel.axis.toGlobalCoord(compModel.axis.getExtent()[0]),
        compModel.axis.toGlobalCoord(compModel.axis.getExtent()[1])
      ];

      // 如果x/y轴设置标签反转, 则同步调整首位标签对应的元素对象和定位
      if (inverse) {
        extentCoord = extentCoord.reverse();
        const temp = firstChild;
        firstChild = lastChild;
        lastChild = temp;
      }

      if (!firstChild.ignore) {
        // 获取计算后的位置
        let firstAttr;
        if (isHor) {
          firstAttr = {
            x: extentCoord[0],
            style: {
              // HACK：利用fontSize的微小变化来触发align的动画，解决单轴上align不触发动画的问题
              fontSize: firstChild.style.fontSize + 0.01,
              align: 'left'
            }
          };
        } else {
          firstAttr = {
            y: extentCoord[0],
            style: {
              verticalAlign: 'bottom',
              // 刻度值贴住了线，为了符合范式故设1px使字偏移 1px
              padding: [0, 0, 1, 0]
            }
          };
        }

        // 根据是否有动画确认是用animation还是用attr
        if (hasAnimation) {
          firstChild.stopAnimation(null, true);
          firstChild.animateTo(firstAttr, animationConfig);
        } else {
          firstChild.attr(firstAttr);
        }
      }

      if (!lastChild.ignore) {
        // 获取计算后的位置
        let lastAttr;
        if (isHor) {
          lastAttr = {
            x: extentCoord[1],
            style: {
              // HACK：利用fontSize的微小变化来触发align的动画，解决单轴上align不触发动画的问题
              fontSize: lastChild.style.fontSize + 0.01,
              align: 'right'
            }
          };
        } else {
          lastAttr = {
            y: extentCoord[1],
            style: {
              verticalAlign: 'top',
              // 刻度值贴住了线，为了符合范式故设1px使字往下走一点
              padding: [1, 0, 0, 0]
            }
          };
        }

        // 根据是否有动画确认是用animation还是用attr
        if (hasAnimation) {
          lastChild.stopAnimation(null, true);
          lastChild.animateTo(lastAttr, animationConfig);
        } else {
          lastChild.attr(lastAttr);
        }
      }

      // handleOverlap
      if (!hideOverlap) {
        return;
      }
      if (!firstChild.ignore) {
        handleHideOverlap(firstChild, labels.slice(1));
      }
      if (!lastChild.ignore) {
        handleHideOverlap(lastChild, labels.slice(0, -1));
      }
    });
  });
}

/**
 * 策略：
 * 1.先判断俩端对齐的label是否是可显示状态
 * 2.如果确认是正常显示，则优先保证俩侧label的显示，对剩余的所有label进行冲突检测
 * 3.如果冲突，则隐藏冲突的label
 */
function handleHideOverlap(target: Text, otherLabels: Text[]) {
  const { x, y, width: targetWidth } = target.getBoundingRect();
  const [targetX] = target.transformCoordToGlobal(x, y);
  otherLabels.forEach(label => {
    if (label.ignore) {
      return;
    }
    const labelRect = label.getBoundingRect();
    const [labelRectX] = label.transformCoordToGlobal(labelRect.x, labelRect.y);
    label.ignore = checkOverlap(targetX, targetWidth, labelRectX, labelRect.width);
  });
}

function checkOverlap(x1: number, width1: number, x2: number, width2: number) {
  // 计算第一个线段的右侧x坐标和第二个线段的左侧x坐标
  const x1Right = x1 + width1;
  const x2Left = x2;

  // 计算第一个线段的左侧x坐标和第二个线段的右侧x坐标
  const x1Left = x1;
  const x2Right = x2 + width2;

  // 检查两个线段是否有重叠
  if ((x1Left <= x2Right && x1Right >= x2Left) || (x2Left <= x1Right && x2Right >= x1Left)) {
    return true; // 重叠
  }
  return false; // 不重叠
}

export function install(registers: EChartsExtensionInstallRegisters) {
  installDvAlignEdge(registers);
}
